import { ParkingCircle, Coffee, DoorOpen, MapPin } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { usePublicConfig } from "@/hooks/usePublicConfig";

interface Props {
  /** Optional title override (used on area pages, e.g. "Parking & arrival in Poplar"). */
  title?: string;
  /** Optional one-line subtitle. */
  subtitle?: string;
}

/**
 * Parking / waiting / reception FAQ block — driven entirely from the
 * admin-editable `compliance.arrivalFaqs` so the same answers appear
 * everywhere they're surfaced. Falls back to defaults when empty.
 */
export function ArrivalFAQ({ title = "Parking, waiting & finding reception", subtitle }: Props) {
  const { compliance } = usePublicConfig();
  const faqs = compliance.arrivalFaqs;
  if (!faqs || faqs.length === 0) return null;

  return (
    <section className="py-16 md:py-20 bg-secondary/40 border-t border-border">
      <div className="mx-auto max-w-5xl px-4 md:px-6">
        <div className="grid lg:grid-cols-[1fr_1.4fr] gap-10 items-start">
          {/* Left: pictograms */}
          <div>
            <p className="text-[11px] font-bold tracking-[0.25em] uppercase text-primary">When you arrive</p>
            <h2 className="mt-3 text-3xl md:text-4xl font-extrabold tracking-tight text-foreground">
              {title}
            </h2>
            <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
              {subtitle ||
                "First time visiting? Here's exactly what to expect when you pull in — including where to park, where to wait, and how to find us."}
            </p>

            <div className="mt-6 grid grid-cols-2 gap-3">
              <ArrivalChip icon={ParkingCircle} label="On-site parking" />
              <ArrivalChip icon={Coffee} label="Indoor wait area" />
              <ArrivalChip icon={DoorOpen} label="Reception ahead" />
              <ArrivalChip icon={MapPin} label={compliance.postalCode} />
            </div>

            <div className="mt-5 rounded-xl border border-border bg-card p-4">
              <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Workshop entrance</p>
              <p className="mt-1.5 text-sm text-foreground leading-snug">
                {compliance.entranceInstructions}
              </p>
            </div>
          </div>

          {/* Right: accordion */}
          <Accordion type="single" collapsible className="rounded-2xl border border-border bg-card divide-y divide-border/60 overflow-hidden">
            {faqs.map((faq, i) => (
              <AccordionItem key={i} value={`arrival-${i}`} className="border-b-0">
                <AccordionTrigger className="px-5 text-left text-[15px] font-semibold text-foreground hover:no-underline py-4">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="px-5 text-sm text-muted-foreground leading-relaxed pb-4">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}

function ArrivalChip({ icon: Icon, label }: { icon: typeof ParkingCircle; label: string }) {
  return (
    <div className="flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2.5">
      <Icon className="h-4 w-4 text-primary shrink-0" />
      <span className="text-xs font-semibold text-foreground truncate">{label}</span>
    </div>
  );
}

/** JSON-LD generator so arrival FAQs feed the same FAQPage schema. */
export function arrivalFaqSchema(faqs: { q: string; a: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}
